import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Trash2, ClipboardList } from "lucide-react";

/**
 * Inline button that admins use to delete and BMs use to request deletion.
 *
 * Props:
 *  - entityType: "client" | "branch"
 *  - entity:     row object (id + name/code)
 *  - onDeleted:  () => void (admin direct delete success)
 *  - onRequested:() => void (BM request submitted)
 *  - testidPrefix?: string
 */
export default function DeleteOrRequestButton({ entityType, entity, onDeleted, onRequested, testidPrefix = "" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user) return null;
  const isAdmin = user.role === "crm_manager";
  const isBM = user.role === "branch_manager";
  if (!isAdmin && !isBM) return null;

  const label = entityType === "client" ? entity.name : `${entity.code} · ${entity.name}`;

  const adminDelete = async () => {
    if (!window.confirm(`Permanently delete "${label}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const url = entityType === "client" ? `/clients/${entity.id}` : `/branches/${entity.id}`;
      await api.delete(url);
      toast.success(`${entityType[0].toUpperCase() + entityType.slice(1)} deleted`);
      onDeleted && onDeleted();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitRequest = async () => {
    if (!reason.trim()) {
      toast.error("Please describe why deletion is needed");
      return;
    }
    setBusy(true);
    try {
      await api.post("/deletion-requests", {
        entity_type: entityType,
        entity_id: entity.id,
        reason: reason.trim(),
      });
      toast.success("Deletion request sent for admin approval");
      setOpen(false);
      setReason("");
      onRequested && onRequested();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (isAdmin) {
    return (
      <button
        onClick={adminDelete}
        disabled={busy}
        className="text-[#C0392B] hover:underline text-xs inline-flex items-center gap-1"
        data-testid={`${testidPrefix}delete-${entityType}-${entity.id}`}
      >
        <Trash2 className="w-3.5 h-3.5" /> Delete
      </button>
    );
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        disabled={busy}
        className="text-[#C0392B] hover:underline text-xs inline-flex items-center gap-1"
        data-testid={`${testidPrefix}request-delete-${entityType}-${entity.id}`}
      >
        <ClipboardList className="w-3.5 h-3.5" /> Request deletion
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="deletion-request-dialog">
          <DialogHeader>
            <DialogTitle>Request deletion — {label}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-xs text-slate-500">
              An admin will review this request before the record is removed. Deletion is blocked if any outstanding billing exists for a client.
            </div>
            <div>
              <Label>Reason *</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                placeholder="e.g. Duplicate record / client offboarded on 2026-04-15"
                data-testid="deletion-request-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={submitRequest}
              disabled={busy || !reason.trim()}
              className="bg-[#C0392B] hover:bg-[#A93226] text-white"
              data-testid="submit-deletion-request"
            >
              {busy ? "Sending…" : "Submit request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
